import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { User, Skull } from 'lucide-react';
import { useNodeClickHandler } from './useNodeClickHandler';

export const SuspectNode = memo(({ data, selected }) => {
  const isVictim = data.isVictim;

  const handleClick = useNodeClickHandler(
    data.onViewDetails,
    () => ({
      type: 'suspect',
      isVictim,
      name: data.label,
      image: data.image,
      occupation: data.occupation,
      alibi: data.alibi,
      background: data.background,
      relationship: data.relationship,
    }),
    data.isDraggingRef
  );

  return (
    <div
      className={`
        relative p-3 rounded-lg shadow-lg min-w-[160px] max-w-[200px] cursor-pointer
        ${isVictim
          ? 'bg-red-950 border-2 border-red-500'
          : 'bg-slate-800 border-2 border-slate-600'
        }
        ${selected ? 'ring-2 ring-white ring-offset-2 ring-offset-transparent' : ''}
        transition-all duration-200 hover:scale-105
      `}
      onClick={handleClick}
    >
      {/* Connection handles - larger hit area for easier connections */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-4 !h-4 !bg-slate-400 !border-2 !border-slate-600 hover:!bg-slate-300 hover:!scale-125 transition-transform"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-4 !h-4 !bg-slate-400 !border-2 !border-slate-600 hover:!bg-slate-300 hover:!scale-125 transition-transform"
      />
      <Handle
        type="target"
        position={Position.Left}
        className="!w-4 !h-4 !bg-slate-400 !border-2 !border-slate-600 hover:!bg-slate-300 hover:!scale-125 transition-transform"
      />
      <Handle
        type="source"
        position={Position.Right}
        className="!w-4 !h-4 !bg-slate-400 !border-2 !border-slate-600 hover:!bg-slate-300 hover:!scale-125 transition-transform"
      />

      {/* Content */}
      <div className="flex flex-col items-center gap-2">
        {/* Image or placeholder */}
        {data.image ? (
          <img
            src={data.image}
            alt={data.label}
            className={`
              w-20 h-20 rounded-full object-cover border-2
              ${isVictim ? 'border-red-500' : 'border-slate-500'}
            `}
          />
        ) : (
          <div
            className={`
              w-20 h-20 rounded-full flex items-center justify-center
              ${isVictim ? 'bg-red-900' : 'bg-slate-700'}
            `}
          >
            {isVictim ? (
              <Skull className="w-10 h-10 text-red-400" />
            ) : (
              <User className="w-10 h-10 text-slate-400" />
            )}
          </div>
        )}

        {/* Name */}
        <div className="text-center">
          <p className="text-sm font-bold text-white truncate max-w-[180px]">
            {data.label}
          </p>
          {isVictim && (
            <p className="text-xs text-red-400 font-semibold">VICTIM</p>
          )}
          {data.occupation && (
            <p className="text-xs text-gray-400 truncate max-w-[180px]">
              {data.occupation}
            </p>
          )}
        </div>
      </div>

      {/* Victim indicator */}
      {isVictim && (
        <div className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1">
          <Skull className="w-3 h-3 text-white" />
        </div>
      )}
    </div>
  );
});

SuspectNode.displayName = 'SuspectNode';

export default SuspectNode;
